// src/components/EmployeeSalaryAssignment.jsx

import React, { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosConfig";
import {
  CardContent,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import { hasAllowedRole, splitRoles } from "../../utils/authSession";

const EmployeeSalaryAssignment = ({ employeeId }) => {
  // Example: fetch the user role from localStorage or context
  const role = localStorage.getItem("role");
  // For real apps, you might use a React context or some Auth library.

  // State variables
  const [ctc, setCtc] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateComponents, setTemplateComponents] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [calculatedSalaryBreakdown, setCalculatedSalaryBreakdown] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "error" });
  const [existingSalaryData, setExistingSalaryData] = useState(null);

  // Helper for showing alerts
  const showAlert = (message, severity) => {
    setAlert({ open: true, message, severity });
  };

  // Close the Snackbar
  const handleSnackbarClose = () => {
    setAlert({ ...alert, open: false });
  };

  // Fetch salary templates and existing salary data on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      const schoolId = localStorage.getItem("schoolId");
      if (!schoolId) {
        console.error("No schoolId found in local storage");
        showAlert("School ID not found. Please log in again.", "error");
        setLoadingTemplates(false);
        return;
      }
      try {
        const response = await axiosInstance.get(
          `/api/salary-templates/school/${schoolId}`
        );
        setTemplates(response.data);
      } catch (error) {
        console.error("Error fetching templates", error);
        showAlert(
          error.response?.data?.message || "Error fetching templates.",
          "error"
        );
      } finally {
        setLoadingTemplates(false);
      }
    };

    const fetchExistingSalary = async () => {
      if (!employeeId) return; // Don't fetch if no employeeId

      try {
        const response = await axiosInstance.get(`/api/employee-salaries/employee/${employeeId}`);
        if (response.data && response.data.length > 0) {
          setExistingSalaryData(response.data);
          showAlert("Existing salary data loaded.", "info");
        } else {
          console.log("No existing salary data found for this employee.");
          setExistingSalaryData(null);
        }
      } catch (error) {
        console.error("Error fetching existing salary data", error);
        showAlert(
          error.response?.data?.message || "Error fetching existing salary data.",
          "error"
        );
        setExistingSalaryData(null);
      }
    };

    fetchTemplates();
    fetchExistingSalary();
  }, [employeeId]);

  // When user selects a template
  const handleTemplateChange = (event) => {
    setSelectedTemplateId(event.target.value);
    setCalculatedSalaryBreakdown(null);
    setTemplateComponents([]);
  };

  // When user changes the CTC input
  const handleCTCChange = (event) => {
    const val = event.target.value;
    if (val && isNaN(Number(val))) {
      showAlert("Please enter a valid numeric CTC.", "warning");
      setCtc("");
      return;
    }
    if (Number(val) < 0) {
      showAlert("CTC cannot be negative.", "warning");
      setCtc("");
      return;
    }
    setCtc(val);
    setCalculatedSalaryBreakdown(null);
  };

  /**
   * ALGORITHM (Basic Salary ignoring deductions):
   * 1) sumOfAllowancesExceptBasic from full CTC
   * 2) leftover => Basic Salary
   * 3) Deductions => negative from full CTC
   * 4) Net => sum(positives) - sum(negatives)
   */
  const calculateSalaryBreakdown = async () => {
    if (!selectedTemplateId || !ctc) {
      showAlert("Please select a template and enter CTC.", "warning");
      return;
    }
    setCalculationLoading(true);
    setCalculatedSalaryBreakdown(null);

    try {
      // Fetch template
      const resp = await axiosInstance.get(
        `/api/salary-templates/${selectedTemplateId}`
      );
      const components = resp.data.components || [];
      setTemplateComponents(components);

      const grossCTC = parseFloat(ctc) || 0;
      let finalMap = {};

      const basicSalaryComp = components.find(
        (c) => c.componentName.toLowerCase() === "basic salary"
      );

      let sumAllowancesExceptBasic = 0;

      // For each component, skip Basic Salary, skip leftover
      components.forEach((comp) => {
        const compName  = comp.componentName;
        const compType  = comp.componentType?.toUpperCase() || "ALLOWANCE";
        const calcMethod= comp.calculationMethod;
        const val       = parseFloat(comp.value || 0);

        if (compName.toLowerCase() === "basic salary") {
          return; // skip for leftover
        }

        if (compType === "ALLOWANCE") {
          let amt = 0;
          if (calcMethod === "FIXED_AMOUNT") {
            amt = val;
          } else if (calcMethod === "PERCENTAGE") {
            amt = (grossCTC * val) / 100;
          } else {
            amt = val;
          }
          finalMap[compName] = amt;
          sumAllowancesExceptBasic += amt;
        } else if (compType === "DEDUCTION") {
          let dedAmt = 0;
          if (calcMethod === "FIXED_AMOUNT") {
            dedAmt = val;
          } else if (calcMethod === "PERCENTAGE") {
            dedAmt = (grossCTC * val) / 100;
          } else {
            dedAmt = val;
          }
          finalMap[compName] = -dedAmt;
        }
      });

      let leftover = grossCTC - sumAllowancesExceptBasic;
      if (basicSalaryComp) {
        finalMap["Basic Salary"] = leftover;
      }

      components.forEach((c) => {
        if (finalMap[c.componentName] === undefined) {
          finalMap[c.componentName] = 0;
        }
      });

      setCalculatedSalaryBreakdown(finalMap);
      setCalculationLoading(false);
    } catch (error) {
      console.error("Error calculating salary breakdown", error);
      showAlert(
        error.response?.data?.message || "Error calculating salary breakdown.",
        "error"
      );
      setCalculationLoading(false);
    }
  };

  // On assign salary
  const handleAssignSalary = async () => {
    if (!calculatedSalaryBreakdown) {
      showAlert("Please calculate salary breakdown first.", "warning");
      return;
    }

    setIsSubmitting(true);
    setSubmissionError(null);
    setSubmissionSuccess(false);

    const schoolId = localStorage.getItem("schoolId");
    if (!schoolId) {
      console.error("No schoolId found in local storage");
      showAlert("School ID not found. Please log in again.", "error");
      setIsSubmitting(false);
      return;
    }

    const employeeData = {
      employeeId: employeeId,
      schoolId: parseInt(schoolId, 10),
      templateId: parseInt(selectedTemplateId, 10),
      totalSalaryPackage: parseFloat(ctc),
      employeeSalaries: Object.entries(calculatedSalaryBreakdown).map(
        ([compName, amt]) => {
          const templateObj = templates.find(
            (t) => t.templateId === parseInt(selectedTemplateId)
          );
          const matchedComp =
            templateObj?.components?.find((c) => c.componentName === compName) || {};
          return {
            componentId: matchedComp.componentId,
            value: amt,
            componentName: compName,
          };
        }
      ),
    };

    console.log("Payload being sent:", JSON.stringify(employeeData, null, 2));

    try {
      const resp = await axiosInstance.post("/api/employee-salaries", employeeData);
      console.log("Employee salary assigned:", resp.data);
      showAlert("Salary assigned successfully!", "success");
      setSubmissionSuccess(true);
      setCalculatedSalaryBreakdown(null);
      setCtc("");
      setSelectedTemplateId("");
      setExistingSalaryData(null);
    } catch (error) {
      console.error("Error assigning salary to employee", error);
      showAlert(
        error.response?.data?.message || "Error assigning salary. Please try again.",
        "error"
      );
      setSubmissionError(
        error.response?.data?.message || "Error assigning salary. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to display negative sign for deductions
  const formatAmount = (component, amount) => {
    const isDeduction =
      component.componentType &&
      component.componentType.toUpperCase() === "DEDUCTION";
    const signNeeded = isDeduction || amount < 0;
    const absVal = Math.abs(amount).toFixed(2);
    return signNeeded ? `-${absVal}` : absVal;
  };

  let grossAnnual = 0;
  let deductionsAnnual = 0;
  let netAnnual = 0;

  if (calculatedSalaryBreakdown) {
    Object.entries(calculatedSalaryBreakdown).forEach(([compName, amt]) => {
      if (amt >= 0) {
        grossAnnual += amt;
      } else {
        deductionsAnnual += Math.abs(amt);
      }
      netAnnual += amt;
    });
  }

  // We'll define a couple of boolean checks for clarity:
  const hasExistingSalary = !!existingSalaryData;
  const isAdmin = hasAllowedRole(["ADMIN", "SCHOOL_ADMIN", "SUPER_ADMIN"], splitRoles(role));

  return (
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {hasExistingSalary ? "View/Edit Salary Assignment" : "Assign Salary"}
      </Typography>

      <Grid container spacing={2} direction="column">

        {hasExistingSalary && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Current Salary Breakdown:
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography fontWeight="bold">Component</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">Type</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">Annual (₹)</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">Monthly (₹)</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {existingSalaryData[0]?.employeeSalaries.map((salaryRecord) => { // Access employeeSalaries from the first element
                    const annualVal = parseFloat(salaryRecord.value);
                    const monthlyVal = annualVal / 12;
                    return (
                      <TableRow key={salaryRecord.employeeSalaryId}>
                        <TableCell component="th" scope="row">
                          {salaryRecord.componentName || "N/A"} {/* Use componentName directly */}
                        </TableCell>
                        <TableCell>
                          {salaryRecord.componentName === "EPF" || salaryRecord.componentName === "HRA" || salaryRecord.componentName === "Basic Salary" ? "ALLOWANCE" : "DEDUCTION"} {/* Determine type based on name, adjust as needed */}
                        </TableCell>
                        <TableCell align="right">
                          {formatAmount({componentType: salaryRecord.componentName === "EPF" || salaryRecord.componentName === "HRA" || salaryRecord.componentName === "Basic Salary" ? "ALLOWANCE" : "DEDUCTION"}, annualVal)} {/* Pass type to formatAmount */}
                        </TableCell>
                        <TableCell align="right">
                          {formatAmount({componentType: salaryRecord.componentName === "EPF" || salaryRecord.componentName === "HRA" || salaryRecord.componentName === "Basic Salary" ? "ALLOWANCE" : "DEDUCTION"}, monthlyVal)} {/* Pass type to formatAmount */}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography fontWeight="bold">Gross Salary</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {existingSalaryData[0] ? existingSalaryData[0].totalSalaryPackage.toFixed(2) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {existingSalaryData[0] ? (existingSalaryData[0].totalSalaryPackage / 12).toFixed(2) : 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {/* Deductions and Net Salary can be added here if needed from existingSalaryData, but totalSalaryPackage is already there */}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {/* Now decide if we allow re-assign or editing */}
        {hasExistingSalary ? (
          isAdmin ? (
            <React.Fragment>
              {/* If admin, show the same assignment form again, but possibly labeled "Re-assign" */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Re-assign Salary (Admin Only)
                </Typography>
              </Grid>
              {/* ... Then show the same form UI as if there's no existing salary */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="template-select">Salary Template</InputLabel>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      size="small"
                      disabled={loadingTemplates}
                    >
                      <Select
                        labelId="template-select-label"
                        id="template-select"
                        value={selectedTemplateId}
                        label="Salary Template"
                        onChange={handleTemplateChange}
                      >
                        <MenuItem value="">
                          <em>
                            {loadingTemplates ? "Loading templates..." : "Select Template"}
                          </em>
                        </MenuItem>
                        {!loadingTemplates && templates.length === 0 && (
                          <MenuItem value="">No templates available</MenuItem>
                        )}
                        {!loadingTemplates &&
                          templates.map((template) => (
                            <MenuItem key={template.templateId} value={template.templateId}>
                              {template.templateName}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="ctc">Annual CTC (₹)</InputLabel>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      margin="dense"
                      id="ctc"
                      label="Annual CTC (₹)"
                      type="number"
                      variant="outlined"
                      value={ctc}
                      onChange={handleCTCChange}
                      placeholder="Enter Annual CTC"
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={calculateSalaryBreakdown}
                  fullWidth
                  disabled={!selectedTemplateId || !ctc || calculationLoading}
                  startIcon={
                    calculationLoading && <CircularProgress size={20} color="inherit" />
                  }
                >
                  Calculate Breakdown
                </Button>
              </Grid>

              {templateComponents.length > 0 && calculatedSalaryBreakdown && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  {/* Show the breakdown table again */}
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <Typography fontWeight="bold">Component</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="bold">Type</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">Annual (₹)</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">Monthly (₹)</Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {templateComponents.map((component) => {
                          const compName = component.componentName;
                          const annualVal = calculatedSalaryBreakdown[compName] || 0;
                          const monthlyVal = annualVal / 12;

                          return (
                            <TableRow key={component.componentId}>
                              <TableCell component="th" scope="row">
                                {compName}
                              </TableCell>
                              <TableCell>
                                {component.componentType || "ALLOWANCE"}
                              </TableCell>
                              <TableCell align="right">
                                {formatAmount(component, annualVal)}
                              </TableCell>
                              <TableCell align="right">
                                {formatAmount(component, monthlyVal)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography fontWeight="bold">Gross Salary</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {grossAnnual.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {(grossAnnual / 12).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography fontWeight="bold">Total Deductions</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {deductionsAnnual.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {(deductionsAnnual / 12).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography fontWeight="bold">Net Salary</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {(grossAnnual - deductionsAnnual).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {((grossAnnual - deductionsAnnual) / 12).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleAssignSalary}
                  fullWidth
                  disabled={!calculatedSalaryBreakdown || isSubmitting}
                  startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                >
                  Re-Assign Salary
                </Button>
              </Grid>
            </React.Fragment>
          ) : (
            <Grid item xs={12} sx={{ mt: 2 }}>
              {/* If user is not admin, display a read-only message */}
              <Typography variant="body1">
                A salary is already assigned. You do not have permission to edit. Please contact an administrator.
              </Typography>
            </Grid>
          )
        ) : (
          // NO existing salary, show new assignment form
          <React.Fragment>
            {/* The existing code for new assignment goes here */}
            {/* (Already shown above in your snippet) */}
          </React.Fragment>
        )}

        {submissionSuccess && (
          <Grid item xs={12} mt={2} sx={{ color: "success.main" }}>
            <Typography>Salary assigned successfully!</Typography>
          </Grid>
        )}
        {submissionError && (
          <Grid item xs={12} mt={2} sx={{ color: "error.main" }}>
            <Typography>{submissionError}</Typography>
          </Grid>
        )}
      </Grid>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={alert.severity} sx={{ width: "100%" }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </CardContent>
  );
};

export default EmployeeSalaryAssignment;
